import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '@/components/Badge';
import type { BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { formatParty, isLocationType } from '@/venue/venue';
import type { Location } from './api';
import { Notice } from './parts';
import { useHelpRequests, useLocations, useManageLive } from './queries';
import {
  formatDuration,
  helpLabel,
  HELP_REQUESTED,
  locationNoun,
  locationStatusLabel,
  locationText,
} from './wording';
import { PageHeader } from '@/components/PageHeader';
import { SegmentedTabs } from '@/components/SegmentedTabs';
import { cardClasses } from '@/components/Card';
import { cn } from '@/lib/cn';
import { SectionHeading } from '@/components/SectionHeading';

type Filter = 'all' | 'in-use' | 'vacant';

const STATUS_TONE: Record<string, BadgeTone> = {
  VACANT: 'neutral',
  OCCUPIED: 'active',
  NEEDS_CLEANING: 'attention',
};

/** "12 tables in 3 zones", "40 seats in 1 zone", or "12 locations in 2 zones" for a mix. */
function summaryText(locations: Location[]): string {
  const types = new Set(locations.map((l) => l.location_type));
  const zones = new Set(locations.map((l) => l.zone_name ?? 'Other'));
  const count = locations.length;
  const only = types.size === 1 ? [...types][0] : undefined;
  const noun = only === 'TABLE' ? 'table' : only === 'SEAT' ? 'seat' : 'location';
  return `${count} ${count === 1 ? noun : `${noun}s`} in ${zones.size} ${zones.size === 1 ? 'zone' : 'zones'}`;
}

function LocationRow({
  location,
  helpTypes,
}: {
  location: Location;
  /** Request types per session, from the assistance read; absent while unknown or when the read failed. */
  helpTypes: Map<string, string[]>;
}) {
  const where = locationText(location.location_type, location.location_label);
  const showCapacity = location.location_type === 'TABLE' && location.nominal_capacity > 0;
  return (
    <li className={cn(cardClasses({ padding: 'none' }), 'flex flex-col gap-2 px-4 py-3')}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-base font-bold break-words text-ink">{where}</p>
          <p className="text-sm text-muted">
            {locationNoun(location.location_type)}
            {showCapacity ? ` · Seats ${location.nominal_capacity}` : ''}
          </p>
        </div>
        <Badge tone={STATUS_TONE[location.status] ?? 'neutral'}>
          {locationStatusLabel(location.status)}
        </Badge>
      </div>
      {location.active_sessions.length > 0 && (
        // Each party is its own tinted block, so two parties sharing a table read as two, not as one list.
        <ul className="flex flex-col gap-1.5">
          {location.active_sessions.map((session) => (
            <li
              key={session.session_id}
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-control bg-surface px-3 py-2 text-sm"
            >
              <span className="min-w-0 break-words text-ink">
                {isLocationType(location.location_type)
                  ? formatParty(
                      location.location_type,
                      session.party_display_name,
                      session.party_size,
                    )
                  : session.party_display_name}
                <span className="text-muted"> · {formatDuration(session.duration_minutes)}</span>
              </span>
              <span className="flex flex-wrap items-center gap-2">
                {session.has_pending_orders && <Badge tone="active">In the kitchen</Badge>}
                {/* Named chips only for request types the assistance read listed; the flag alone, or a failed
                    read, shows the neutral chip, never a guess. */}
                {session.has_active_assistance &&
                  (helpTypes.get(session.session_id)?.length
                    ? [...new Set(helpTypes.get(session.session_id)!.map(helpLabel))].map(
                        (chip) => (
                          <Badge key={chip} tone="attention">
                            {chip}
                          </Badge>
                        ),
                      )
                    : [
                        <Badge key="help" tone="attention">
                          {HELP_REQUESTED}
                        </Badge>,
                      ])}
                <Link
                  to={`/staff/sessions/${session.session_id}`}
                  aria-label={`View session at ${where}`}
                  className="inline-flex min-h-10 items-center font-semibold text-primary"
                >
                  View session
                </Link>
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function LocationsPage() {
  useManageLive();
  const query = useLocations();
  const [filter, setFilter] = useState<Filter>('all');
  const locations = useMemo(() => query.data ?? [], [query.data]);
  // Read the open requests only while some session is flagged, to name what was asked for.
  const anyHelp = locations.some((l) => l.active_sessions.some((s) => s.has_active_assistance));
  const help = useHelpRequests(anyHelp);
  const helpTypes = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const r of help.data ?? []) {
      if (r.status !== 'PENDING') continue;
      map.set(r.session_id, [...(map.get(r.session_id) ?? []), r.type]);
    }
    return map;
  }, [help.data]);

  const groups = useMemo(() => {
    const shown = locations.filter((l) => {
      const inUse = l.status === 'OCCUPIED' || l.active_sessions.length > 0;
      if (filter === 'in-use') return inUse;
      if (filter === 'vacant') return l.status === 'VACANT' && l.active_sessions.length === 0;
      return true;
    });
    const byZone = new Map<string, Location[]>();
    for (const location of shown) {
      const zone = location.zone_name ?? 'Other';
      byZone.set(zone, [...(byZone.get(zone) ?? []), location]);
    }
    return [...byZone.entries()];
  }, [locations, filter]);

  const cinema = locations.length > 0 && locations.every((l) => l.location_type === 'SEAT');
  const noun = cinema ? 'seats' : 'tables';
  const filters: { id: Filter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'in-use', label: 'In use' },
    { id: 'vacant', label: 'Vacant' },
  ];

  return (
    <section aria-labelledby="locations-title" className="flex flex-col gap-3">
      <PageHeader
        id="locations-title"
        title="Locations"
        subtitle={query.data && locations.length > 0 ? summaryText(locations) : undefined}
        actions={
          <Button
            variant="secondary"
            loading={query.isFetching && !query.isPending}
            onClick={() => void query.refetch()}
          >
            Refresh
          </Button>
        }
      />
      <p className="text-sm text-muted">
        This list is read-only. Tables, seats, zones and QR codes can&apos;t be changed from here
        yet.
      </p>

      <SegmentedTabs
        label="Show"
        semantics="buttons"
        options={filters}
        value={filter}
        onChange={setFilter}
      />

      {query.isPending && (
        <LoadingRegion slowHint label="Loading locations">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        </LoadingRegion>
      )}
      {query.isError && !query.data && (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      )}
      {query.isError && query.data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the locations. Showing the last list received.
        </Notice>
      )}
      {query.data && locations.length === 0 && (
        <EmptyState
          icon="pin"
          title="No locations yet"
          message="This branch has no tables or seats set up."
        />
      )}
      {query.data && locations.length > 0 && groups.length === 0 && (
        <EmptyState
          icon="pin"
          title={
            filter === 'vacant' ? `No vacant ${noun} right now` : `No ${noun} in use right now`
          }
          message={`Every ${noun.slice(0, -1)} is ${filter === 'vacant' ? 'in use' : 'vacant'}. Choose All to see them.`}
        />
      )}
      {/* Each zone runs the full width; its locations are cards in the grid the loading skeleton draws. */}
      <div className="flex flex-col gap-4">
        {groups.map(([zone, items]) => (
          <section key={zone} aria-labelledby={`zone-${zone}`} className="flex flex-col gap-2">
            <SectionHeading id={`zone-${zone}`} count={items.length}>
              {zone}
            </SectionHeading>
            <ul className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
              {items.map((location) => (
                <LocationRow key={location.location_id} location={location} helpTypes={helpTypes} />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </section>
  );
}
